import { Row, Card, Space, Col } from 'antd'
import Link from 'next/link'
import dayjs from 'dayjs'
import dynamic from 'next/dynamic'
import { GetServerSideProps } from 'next'
import { ScheduleOutlined, TrophyOutlined, TeamOutlined, FormOutlined, FileTextOutlined, ProfileOutlined, DesktopOutlined, ClockCircleOutlined, SettingOutlined, TableOutlined } from '@ant-design/icons'
import SimpleBackground from '@/components/SimpleBackground'
import { useMatch } from '@/hooks/swr/useMatch'
import useAdmin from '@/hooks/swr/useAdmin'
import { isSinglePlayerMatch, matchTypeMap } from '@/utils/utils'
import { serverGet } from '@/lib/serverApi'
import { ORG_ID, absUrl, breadcrumbLd, sportOfMatchType, SeoProps } from '@/utils/seo'

const Map = dynamic(() => import('@/components/Map'), { ssr: false })

// 赛事概况:一段自然语言摘要,搜索引擎摘要和 AI 回答「某某杯什么时候、在哪、多少队」可直接引用。不含任何选手信息。
const matchSummary = (m) => {
  if (!m?.name) return ''
  const sport = sportOfMatchType(m.match_type)
  const typeLabel = matchTypeMap[m.match_type] || ''
  const parts = [`${m.name}是一场${sport}比赛${typeLabel ? `(赛制:${typeLabel})` : ''}`]
  if (m.match_time) parts.push(`比赛时间为 ${dayjs(m.match_time).format('YYYY 年 M 月 D 日')}`)
  if (m.location) parts.push(`比赛地点在${m.location}`)
  if (m.teams_count) parts.push(`共有 ${m.teams_count} 支${isSinglePlayerMatch(m.match_type) ? '选手' : '队伍'}报名参赛`)
  if (m.turn) parts.push(`赛程共 ${m.turn} 轮`)
  const units = [
    ['主办单位', m.organize_unit],
    ['承办单位', m.undertake_unit],
    ['协办单位', m.co_organize_unit],
    ['指导单位', m.guide_unit],
    ['赞助单位', m.sponsor_unit],
  ].filter(([, v]) => v)
  return `${parts.join(',')}。${units.map(([k, v]) => `${k}:${v}。`).join('')}比赛使用灵缇赛事系统编排与计分,赛程对阵与成绩排名可在本页查看。`
}

const MatchInfo = ({ match_id, initialMatch = null }: { match_id: string; initialMatch?: any }) => {
  const { data: swrMatch } = useMatch(match_id)
  // 服务端预取的比赛信息先顶上(爬虫可读),客户端 SWR 取回后以实时数据为准
  const match = swrMatch?.name ? swrMatch : initialMatch ?? swrMatch
  const summary = matchSummary(match)
  const { isAdmin } = useAdmin(match_id)

  const single = isSinglePlayerMatch(match?.match_type)
  const scoreHref = single ? 'players_score' : 'teams_score'
  const listHref = single ? 'players' : 'teams'

  // 9 宫格功能入口(对标乐动力赛事详情枢纽),映射现有页面
  const entries = [
    { label: '赛程对阵', href: 'all_rounds', icon: <ScheduleOutlined /> },
    ...(match?.match_type === 'pickleball_group_elimination' ? [{ label: '签表', href: 'group_table', icon: <TableOutlined /> }] : []),
    { label: '成绩排名', href: scoreHref, icon: <TrophyOutlined /> },
    { label: '参赛名单', href: listHref, icon: <TeamOutlined /> },
    { label: '我要报名', href: 'entry', icon: <FormOutlined /> },
    { label: '赛事规程', href: 'rule', icon: <FileTextOutlined /> },
    { label: '赛事文档', href: 'document', icon: <ProfileOutlined /> },
    ...(isAdmin
      ? [
          { label: '投屏直播', href: 'presentation', icon: <DesktopOutlined /> },
          { label: '比赛计时', href: 'timer', icon: <ClockCircleOutlined /> },
          { label: '比赛设置', href: 'settings', icon: <SettingOutlined /> },
        ]
      : []),
  ]

  return (
    <div className="relative h-full">
      {match?.poster ? (
        <>
          <div className="fixed inset-0 -z-10 bg-cover bg-center" style={{ backgroundImage: `url(${match.poster})` }} />
          <div className="fixed inset-0 -z-10 bg-black/50" />
        </>
      ) : (
        <SimpleBackground />
      )}
      <div className="relative overflow-auto px-2 py-12">
        <Row>
          <Col lg={{ span: 12, push: 6 }} xs={24}>
            {/* Hero 头部 */}
            <div className="mb-8 text-center text-white">
              <h1 className="mb-2 text-3xl font-bold text-white lg:text-4xl">{match?.name}</h1>
              <div className="text-sm text-white/85">
                {match?.match_time ? dayjs(match.match_time).format('YYYY年MM月DD日 HH:mm') : '时间待定'}
                {match?.location ? ` · ${match.location}` : ''}
              </div>
            </div>

            {/* 9 宫格功能入口 */}
            <Card className="mb-6">
              <div className="grid grid-cols-3 gap-3">
                {entries.map((e) => (
                  <Link href={`/match/${match_id}/${e.href}`} key={e.label}>
                    <div className="flex flex-col items-center gap-2 rounded-xl bg-gray-50 p-4 transition hover:-translate-y-0.5 hover:bg-gray-100 hover:shadow-md">
                      <span className="accent-text text-2xl">{e.icon}</span>
                      <span className="text-sm font-medium text-gray-700">{e.label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>

            {summary && (
              <Card className="mb-6">
                <h2 className="m-0 mb-2 text-base font-bold">赛事概况</h2>
                <p className="m-0 leading-relaxed text-gray-700">{summary}</p>
              </Card>
            )}

            {/* 比赛信息详情 */}
            <Card className="m-auto">
              <div className="mb-6 text-center text-xl font-bold">赛事信息</div>
              <div className="mb-6 grid grid-cols-1 gap-x-4 leading-7 lg:grid-cols-2">
                <div className="mb-4">
                  <div>比赛时间:</div>
                  <div className="font-bold">{match.match_time ? dayjs(match.match_time).format('YYYY年MM月DD日HH时mm分') : '待定'}</div>
                </div>
                <div className="mb-4">
                  <div>比赛地点:</div>
                  <div className="font-bold">{match.location ?? '待定'}</div>
                </div>
                <div className="mb-4">
                  <div>比赛收费:</div>
                  <div className="font-bold">{match.entry_fee / 100}元</div>
                </div>
                <div className="mb-4">
                  <div>比赛轮数:</div>
                  <div className="font-bold">{match.turn}轮</div>
                </div>
                <div className="mb-4">
                  <div>比赛奖项:</div>
                  <Space className="gap-4 font-bold">
                    <div>一等奖{match.first_prize}名</div>
                    <div>二等奖{match.second_prize}名</div>
                    <div>三等奖{match.third_prize}名</div>
                  </Space>
                </div>
                <div className="mb-4">
                  <div>是否计入天梯分:</div>
                  <div className="font-bold">{match.is_count_elo_rank ? '是' : '否'}</div>
                </div>
              </div>
              <div className="mb-4 w-full">{match.lng && match.lat && <Map lng={match.lng} lat={match.lat} location={match.location} />} </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

// 只有与「全部比赛」匿名可见范围一致的比赛(公开、未隐藏、非测试)且名称像样才允许收录,
// 其余(私密/测试/「23号比赛」这类临时名/不足 4 队的练习局)一律 noindex。
const indexable = (m) =>
  !!m?.name && m.is_private === false && !m.hidden && !m.is_test_match && !m.deleted_at && (m.teams_count ?? 0) >= 4 && m.name.length >= 6 && /[\u4e00-\u9fa5]{3,}/.test(m.name)

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const match_id = String(ctx.query.match_id ?? '')
  const res = await serverGet<any>(`matches/${encodeURIComponent(match_id)}`)
  const m = res?.ok ? res.match : null
  if (!m) return { props: { hide_menu: true, match_id } }

  const path = `/match/${match_id}/info`
  const sport = sportOfMatchType(m.match_type)
  const description = matchSummary(m).slice(0, 160)
  const start = m.match_time || m.created_at
  // 只放赛事概况需要的字段进 props,不把整份比赛数据(含管理员手机号等)序列化进 HTML
  const initialMatch = {
    id: m.id,
    name: m.name,
    match_type: m.match_type,
    match_time: m.match_time ?? null,
    location: m.location ?? null,
    teams_count: m.teams_count ?? null,
    turn: m.turn ?? null,
    entry_fee: m.entry_fee ?? 0,
    first_prize: m.first_prize ?? 0,
    second_prize: m.second_prize ?? 0,
    third_prize: m.third_prize ?? 0,
    is_count_elo_rank: !!m.is_count_elo_rank,
    poster: m.poster ?? null,
    organize_unit: m.organize_unit ?? null,
    undertake_unit: m.undertake_unit ?? null,
    co_organize_unit: m.co_organize_unit ?? null,
    guide_unit: m.guide_unit ?? null,
    sponsor_unit: m.sponsor_unit ?? null,
  }
  const seo: SeoProps = {
    title: `${m.name} · ${sport}比赛赛程与成绩排名 | 灵缇赛事系统`,
    description,
    keywords: [m.name, `${sport}比赛`, `${sport}比赛成绩`, m.location, m.organize_unit, '灵缇赛事系统'].filter(Boolean).join(','),
    image: m.poster || undefined,
    path,
    noindex: !indexable(m),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: m.name,
        description,
        sport,
        startDate: start,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: { '@type': 'Place', name: m.location || '线下赛场', address: m.location || undefined },
        image: m.poster ? [m.poster] : [absUrl('/lingti-gd-logo.png')],
        url: absUrl(path),
        ...(m.organize_unit ? { organizer: { '@type': 'Organization', name: m.organize_unit } } : {}),
        ...(m.sponsor_unit ? { sponsor: { '@type': 'Organization', name: m.sponsor_unit } } : {}),
        ...(m.entry_fee > 0
          ? { offers: { '@type': 'Offer', price: (m.entry_fee / 100).toFixed(2), priceCurrency: 'CNY', url: absUrl(`/match/${match_id}/entry`) } }
          : {}),
        provider: { '@id': ORG_ID },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['赛事库', '/events'],
        [m.name, path],
      ]),
    ],
  }
  ctx.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=3600')
  return { props: { hide_menu: true, match_id, initialMatch, seo } }
}

export default MatchInfo
